import {BASE_URL} from "./index.ts";
import {get, post, put, _delete, patch} from "../utils/http.ts";

export type OrganizationRole = 'member' | 'admin';

export type EmrConfig = {
  id: number;
  emr_type: string;
  enabled: boolean;
}

export type Organization = {
  id: number;
  name: string;
  slug: string;
  role?: OrganizationRole;
  member_count?: number;
  is_current?: boolean;
  emr_config?: EmrConfig | null;
  created_at: string;
  updated_at: string;
}

export type OrganizationMember = {
  id: number;
  user_id: number;
  user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  role: OrganizationRole;
  created_at: string;
}

export type OrganizationInvitation = {
  id: number;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  expires_at: string;
  created_at: string;
  invited_by?: {
    id: number;
    name: string;
  };
}

export type InvitationDetails = {
  organization_name: string;
  email: string;
  expired: boolean;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
}

export async function getOrganizations(): Promise<Organization[]> {
  return get<Organization[]>(new URL(`${BASE_URL}/organizations`));
}

export async function getOrganization(id: number): Promise<Organization> {
  return get<Organization>(new URL(`${BASE_URL}/organizations/${id}`));
}

export async function createOrganization(name: string): Promise<Organization> {
  return post<Organization>(new URL(`${BASE_URL}/organizations`), {
    body: {name}
  });
}

export async function updateOrganization(id: number, name: string): Promise<Organization> {
  return put<Organization>(new URL(`${BASE_URL}/organizations/${id}`), {
    body: {name}
  });
}

export async function deleteOrganization(id: number): Promise<void> {
  return _delete<void>(new URL(`${BASE_URL}/organizations/${id}`));
}

export async function switchOrganization(id: number): Promise<Organization> {
  return post<Organization>(new URL(`${BASE_URL}/organizations/${id}/switch`), {});
}

export async function getOrganizationMembers(organizationId: number): Promise<OrganizationMember[]> {
  return get<OrganizationMember[]>(new URL(`${BASE_URL}/organizations/${organizationId}/members`));
}

export async function addOrganizationMember(organizationId: number, email: string): Promise<OrganizationMember | OrganizationInvitation> {
  return post<OrganizationMember | OrganizationInvitation>(new URL(`${BASE_URL}/organizations/${organizationId}/members`), {
    body: {email}
  });
}

export async function updateOrganizationMember(organizationId: number, memberId: number, role: OrganizationRole): Promise<OrganizationMember> {
  return patch<OrganizationMember>(new URL(`${BASE_URL}/organizations/${organizationId}/members/${memberId}`), {
    body: {role}
  });
}

export async function removeOrganizationMember(organizationId: number, memberId: number): Promise<void> {
  return _delete<void>(new URL(`${BASE_URL}/organizations/${organizationId}/members/${memberId}`));
}

export async function getOrganizationInvitations(organizationId: number): Promise<OrganizationInvitation[]> {
  return get<OrganizationInvitation[]>(new URL(`${BASE_URL}/organizations/${organizationId}/invitations`));
}

export async function cancelInvitation(organizationId: number, invitationId: number): Promise<void> {
  return _delete<void>(new URL(`${BASE_URL}/organizations/${organizationId}/invitations/${invitationId}`));
}

export async function resendInvitation(organizationId: number, invitationId: number): Promise<OrganizationInvitation> {
  return post<OrganizationInvitation>(new URL(`${BASE_URL}/organizations/${organizationId}/invitations/${invitationId}/resend`), {});
}

export async function getInvitationDetails(token: string): Promise<InvitationDetails> {
  return get<InvitationDetails>(new URL(`${BASE_URL}/invitations/${token}`));
}

export async function acceptInvitation(token: string): Promise<Organization> {
  return post<Organization>(new URL(`${BASE_URL}/invitations/${token}/accept`), {});
}

export type SignupFromInvitationResponse = {
  user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  token: string;
}

export async function signupFromInvitation(
  token: string,
  password: string,
  firstName?: string,
  lastName?: string
): Promise<SignupFromInvitationResponse> {
  return post<SignupFromInvitationResponse>(new URL(`${BASE_URL}/invitations/${token}/signup`), {
    body: {
      password,
      first_name: firstName,
      last_name: lastName
    }
  });
}

export async function checkUserExists(email: string): Promise<boolean> {
  const response = await get<{ exists: boolean }>(new URL(`${BASE_URL}/users/exists?email=${encodeURIComponent(email)}`));
  return response.exists;
}
